import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore, ROL } from '@/store/auth.store';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';
import logoHorizontal from '@/assets/logo-horizontal.svg';

const orbKeyframes = `
  @keyframes orbFloatY2      { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }
  @keyframes orbHaloBreathe  { 0%,100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 0.85; transform: scale(1.1); } }
  @keyframes orbAuroraSpin   { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes orbShadowBreathe{ 0%,100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(0.86); opacity: 0.25; } }
  @keyframes orbit1 { from { transform: rotate(0deg)   translateX(98px)  rotate(0deg);    } to { transform: rotate(360deg)  translateX(98px)  rotate(-360deg); } }
  @keyframes orbit2 { from { transform: rotate(180deg) translateX(112px) rotate(-180deg); } to { transform: rotate(-180deg) translateX(112px) rotate(180deg);  } }
  @keyframes orbit3 { from { transform: rotate(20deg)  translateX(104px) rotate(-20deg);  } to { transform: rotate(380deg)  translateX(104px) rotate(-380deg); } }
`;

function IkalOrb() {
  return (
    <>
      <style>{orbKeyframes}</style>
      <div style={{ position: 'relative', width: 280, height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Ground shadow */}
        <div style={{ position: 'absolute', width: 150, height: 26, bottom: 38, borderRadius: '50%', background: 'radial-gradient(ellipse,rgba(0,0,0,0.55),rgba(0,0,0,0) 70%)', animation: 'orbShadowBreathe 5.4s ease-in-out infinite' }} />
        {/* Halo */}
        <div style={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle,rgba(193,105,79,0.24),rgba(193,105,79,0) 65%)', filter: 'blur(4px)', animation: 'orbHaloBreathe 4.6s ease-in-out infinite' }} />
        {/* Orbiting particles */}
        <div style={{ position: 'absolute', width: 7, height: 7, borderRadius: '50%', background: '#F3E9D8', boxShadow: '0 0 12px 4px rgba(243,233,216,0.75)', animation: 'orbit1 7s linear infinite' }} />
        <div style={{ position: 'absolute', width: 5, height: 5, borderRadius: '50%', background: '#9FDAD0', boxShadow: '0 0 9px 3px rgba(159,218,208,0.65)', animation: 'orbit2 9s linear infinite' }} />
        <div style={{ position: 'absolute', width: 4, height: 4, borderRadius: '50%', background: '#E8A98A', boxShadow: '0 0 8px 3px rgba(232,169,138,0.6)', animation: 'orbit3 12.5s linear infinite' }} />
        {/* Orb sphere */}
        <div style={{ position: 'relative', width: 176, height: 176, borderRadius: '50%', overflow: 'hidden', boxShadow: '0 26px 50px rgba(0,0,0,0.5)', animation: 'orbFloatY2 5.4s cubic-bezier(.45,0,.55,1) infinite' }}>
          <div style={{ position: 'absolute', inset: 0, background: '#0F1D1B' }} />
          <div style={{ position: 'absolute', width: '230%', height: '230%', top: '-65%', left: '-65%', animation: 'orbAuroraSpin 22s linear infinite' }}>
            <div style={{ position: 'absolute', width: '56%', height: '56%', top: '12%', left: '10%', borderRadius: '50%', background: '#6FBBAF', filter: 'blur(24px)', opacity: 0.9 }} />
            <div style={{ position: 'absolute', width: '50%', height: '50%', top: '50%', left: '54%', borderRadius: '50%', background: '#C1694F', filter: 'blur(24px)', opacity: 0.85 }} />
          </div>
          {/* Specular highlight */}
          <div style={{ position: 'absolute', top: 20, left: 26, width: 46, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,0.5)', filter: 'blur(9px)' }} />
          {/* Inner shadow rim */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', boxShadow: 'inset -20px -20px 42px rgba(0,0,0,0.55), inset 12px 12px 22px rgba(255,255,255,0.12)' }} />
          {/* Teal border */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', boxShadow: 'inset 0 0 0 1px rgba(159,218,208,0.22)' }} />
        </div>
      </div>
    </>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/login', { email, password });
      setAuth(data.usuario);
      navigate(data.usuario.rolId === ROL.SUPER_ADMIN ? '/super-admin' : '/dashboard');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Credenciales incorrectas. Verifica tu email y contraseña.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#f0f2f8] px-4 py-8">
      {/* Floating card */}
      <div className="w-full max-w-[880px] rounded-2xl shadow-2xl overflow-hidden flex min-h-[520px]">

        {/* ── Left: form ── */}
        <div className="flex flex-col justify-between w-full lg:w-1/2 bg-white px-10 py-10">
          <div className="flex items-center gap-2">
            <img src={logoHorizontal} alt="Anik" className="h-14" />
          </div>

          <div className="mt-5 mb-4">
            <h1 className="text-2xl font-bold text-text-primary mb-1">Bienvenido</h1>
            <p className="text-sm text-text-secondary mb-7">Ingresa tus credenciales para continuar.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Correo electrónico
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-border text-sm text-text-primary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition"
                  placeholder="tu@correo.com"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-text-primary">Contraseña</label>
                  <button
                    type="button"
                    onClick={() => navigate('/olvide-password')}
                    className="text-xs text-primary-500 hover:text-primary-700 font-medium transition-colors"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-border text-sm text-text-primary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition"
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 active:bg-primary-700 transition-colors disabled:opacity-60 mt-1"
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              </button>
            </form>
          </div>

          <p className="text-[11px] text-gray-400">© 2026 Anik</p>
        </div>

        {/* ── Right: orb panel ── */}
        <div
          className="hidden lg:flex lg:w-1/2 items-center justify-center"
          style={{ background: 'linear-gradient(160deg, #0F1B24, #1B2A3A)' }}
        >
          <IkalOrb />
        </div>
      </div>
    </div>
  );
}
